// Items: finished products (garments), fabrics and trims, with bills of materials and costing.
// Shared by the server (which enforces it) and the screens.

export const ITEM_TYPES = ["product", "fabric", "trim"] as const;
export type ItemType = (typeof ITEM_TYPES)[number];
export const ITEM_TYPE_LABEL: Record<ItemType, string> = { product: "Garment", fabric: "Fabric", trim: "Trim / Accessory" };
export const ITEM_TYPE_PLURAL: Record<ItemType, string> = { product: "Garments", fabric: "Fabrics", trim: "Trims & Accessories" };
/** Which v2.0.41 permission page controls each item type. */
export const ITEM_PAGE: Record<ItemType, string> = { product: "products", fabric: "fabric", trim: "products" };

export const UNITS = ["pcs", "m", "yd", "kg", "g", "roll", "set", "pair", "cone"] as const;
export const DEFAULT_UNIT: Record<ItemType, string> = { product: "pcs", fabric: "m", trim: "pcs" };
export const REPLENISHMENT = [["bulk", "Bulk / stock"], ["mto", "Made to order"], ["none", "Not linked"]] as const;

// Fibre composition fields, exactly as in v2.0.41.
export const FABRIC_COMP_FIELDS: ReadonlyArray<readonly [string, string]> = [
  ["cotton", "Cotton"], ["polyester", "Polyester"], ["nylon", "Nylon"], ["aramid", "Aramid"], ["metaAramid", "Meta-Aramid"],
  ["modaAcrylic", "Moda Acrylic"], ["paraAramid", "Para-Aramid"], ["frViscose", "FR Viscose"], ["originalLenzing", "Original Lenzing"],
  ["chineseLenzing", "Chinese Lenzing"], ["ast", "AST"], ["nega", "Nega"], ["lycra", "Lycra"],
];

// Setup lists (Settings → Database). Keys match v2.0.41's db.database where they existed.
export const LIST_DEFS: ReadonlyArray<{ key: string; label: string; extras?: string[]; icon?: boolean }> = [
  { key: "brand", label: "Brand", icon: true, extras: ["code", "primaryColor", "secondaryColor", "footerText", "tagline"] },
  { key: "category", label: "Category", extras: ["code"] },
  { key: "size", label: "Size", extras: ["code"] },
  { key: "colourName", label: "Colour", extras: ["code", "pantone", "hex"], icon: true },
  { key: "speciality", label: "Speciality" },
  { key: "standard", label: "Standard", icon: true },
  { key: "cat", label: "CAT" },
  { key: "atpv", label: "ATPV" },
  { key: "cvc", label: "CVC" },
  { key: "structure", label: "Fabric Structure" },
  { key: "productFeature", label: "Product Feature" },
  { key: "washCare", label: "Wash Care", icon: true },
  { key: "trimType", label: "Trim Type" },
  { key: "season", label: "Season / Collection" },
  { key: "gender", label: "Gender" },
  { key: "fit", label: "Fit" },
];
export const LIST_KEYS = new Set(LIST_DEFS.map((d) => d.key));

export interface ListEntry {
  value: string; pantone?: string; hex?: string; iconFileId?: string; sizes?: string[];
  primaryColor?: string; secondaryColor?: string; footerText?: string;   // brands (datasheets)
  code?: string;                                                         // brand code prefix / category code (garment codes)
  inactive?: boolean;                                                    // deactivated: kept on records, not offered for new ones
  sizeCodes?: Record<string, string>;                                    // categories: size → code in SKUs (default: the size itself)
  // Standards & certification marks (Stage 2.6: the single source for the Artwork Library and labels)
  title?: string; edition?: string;                                      // full name, e.g. "Protection against heat and flame"; edition "2015"
  markType?: "standard" | "mark";                                        // a test standard, or a mark only (CE, UKCA, pictogram, recycling…)
  showOnGarments?: boolean;                                              // marks only: offered in the garment standards picker too
  company?: StandardCompanyFields;                                       // this organisation's own certificate details (never copied)
  // Care symbols
  meaning?: string;                                                      // e.g. "Wash at 60 °C, normal process"
  careGroup?: string;                                                    // washing, bleaching, drying, ironing, professional
  careType?: "dry" | "wet";
  /** Wash care: the organisation's selection group this symbol is in (see CareSelectionGroup), if any. */
  selGroup?: string;
  /** Brand: a short tagline for swing tags (translations in Settings → Languages & translations). */
  tagline?: string;
  /** Order inside its selection group (lower first); ties keep the Settings list order. */
  selOrder?: number;
  /** Care symbols and standard / certification marks: may the label designer print it in another colour? Empty = detected
   *  from the picture (one colour: yes; several colours, e.g. the red-and-black NFPA marks: no). */
  recolour?: boolean;
  /** Standards: which list the garment form shows it in ("en" = EN / ISO / IEC, "other" = other standards and ratings).
   *  Empty = from the code (EN, ISO or IEC at the start → "en"). */
  section?: "en" | "other";                                              // professional care: dry clean or wet clean
  // Brand logos
  monoFileId?: string;                                                   // one-colour (black) version for carton / flexo printing
  usage?: string[];                                                      // labels, packaging, documents
}
export interface StandardCompanyFields { certNo?: string; licenceNo?: string; nbCertNo?: string; docRef?: string; expiry?: string }
export const COMPANY_FIELDS: Array<[keyof StandardCompanyFields, string]> = [
  ["certNo", "Certificate number"], ["licenceNo", "Licence number"], ["nbCertNo", "Notified body certificate no."], ["docRef", "DoC reference"], ["expiry", "Expiry date"],
];
export const CARE_GROUPS: Array<[string, string]> = [["washing", "Washing"], ["bleaching", "Bleaching"], ["drying", "Drying"], ["ironing", "Ironing"], ["professional", "Professional care"]];
export const LOGO_USAGE: Array<[string, string]> = [["labels", "Labels & tags"], ["packaging", "Carton & packaging"], ["documents", "Documents"]];
/** A standard's code for matching duplicates: case, spaces and the edition year don't count ("EN ISO 11612:2015" = "en iso 11612"). */
export const standardKey = (code: string) => String(code || "").toLowerCase().replace(/[\s:/-]*(19|20)\d{2}([-/]\d{1,2})?$/, "").replace(/[^a-z0-9]/g, "");
/** How a standard or mark is described on its tile: "EN ISO 11612:2015 – Protection against heat and flame". */
export const standardLabel = (e: ListEntry) => `${e.value}${e.edition && !e.value.includes(e.edition) ? `:${e.edition}` : ""}${e.title ? ` – ${e.title}` : ""}`;
/** Offered in the garment standards picker: test standards, and marks ticked "Show on garments". */
export const onGarmentPicker = (e: ListEntry) => e.markType !== "mark" || !!e.showOnGarments;
/** A care symbol's meaning; a code-like name with no meaning is flagged "Name missing". */
export const careMeaning = (e: ListEntry) => e.meaning || e.value;
export const careNameMissing = (e: ListEntry) => !e.meaning && (!/\s/.test(e.value) || /\.(png|jpe?g|svg|webp)$/i.test(e.value));

/**
 * Where each list's values are used, so renaming an entry can update every record (as v2.0.41).
 * field: "category" = the item's category column; "data.x" = a text value; "data.x[]" = a list of values.
 */
export const LIST_USAGE: Record<string, Array<{ type: ItemType; field: string }>> = {
  brand: [{ type: "product", field: "data.brand" }],
  category: [{ type: "product", field: "category" }],
  size: [{ type: "product", field: "data.sizes[]" }],
  trimType: [{ type: "trim", field: "category" }],
  colourName: [{ type: "product", field: "data.colours[]" }, { type: "fabric", field: "data.colour" }, { type: "trim", field: "data.colour" }],
  standard: [{ type: "product", field: "data.standards[]" }],
  productFeature: [{ type: "product", field: "data.features[]" }],
  washCare: [{ type: "product", field: "data.washCare[]" }],
  season: [{ type: "product", field: "data.season" }],
  gender: [{ type: "product", field: "data.gender" }],
  fit: [{ type: "product", field: "data.fit" }],
  speciality: [{ type: "fabric", field: "data.speciality" }, { type: "product", field: "data.speciality" }],
  cat: [{ type: "fabric", field: "data.cat" }],
  atpv: [{ type: "fabric", field: "data.atpv" }],
  cvc: [{ type: "fabric", field: "data.cvc" }],
  structure: [{ type: "fabric", field: "data.structure" }],
};
/** A–Z, ignoring case, with numbers in natural order (ATPV 8 before ATPV 10). */
export const naturalSort = (values: string[]) => [...values].sort((a, b) => a.localeCompare(b, "en", { numeric: true, sensitivity: "base" }));
/** Which list a standard shows in on the garment form. */
export const standardSection = (e: Pick<ListEntry, "value" | "section">): "en" | "other" => e.section || (/^(EN|ISO|IEC)\b/i.test(e.value.trim()) ? "en" : "other");
export const STANDARD_SECTIONS: Array<["en" | "other", string]> = [["en", "EN / ISO / IEC"], ["other", "Other standards and ratings"]];

/** Letter sizes in wearing order; XXL = 2XL and XXXL = 3XL rank together. */
const LETTER_SIZES = ["XXXS", "3XS", "XXS", "2XS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "XXXL", "3XL", "XXXXL", "4XL", "5XL", "6XL", "7XL", "8XL", "9XL", "10XL"];
const letterRank = (v: string) => {
  const u = v.toUpperCase().replace(/\s+/g, "");
  const i = LETTER_SIZES.indexOf(u);
  if (i < 0) return -1;
  return ({ XXL: 9, "2XL": 9, XXXL: 11, "3XL": 11, XXXXL: 13, "4XL": 13 } as Record<string, number>)[u] ?? i;
};
/** Sizes in size order: letter sizes (XS, S, M … 8XL), then numeric sizes ascending (28, 30, 104…), then the rest A–Z. */
export function sortSizes(values: string[]): string[] {
  const key = (v: string): [number, number, string] => {
    const l = letterRank(v);
    if (l >= 0) return [0, l, v];
    const n = parseFloat(v.replace(",", "."));
    if (/^\d/.test(v.trim()) && Number.isFinite(n)) return [1, n, v];
    return [2, 0, v.toLowerCase()];
  };
  return [...new Set(values.map((v) => String(v).trim()).filter(Boolean))].sort((a, b) => {
    const x = key(a), y = key(b);
    return x[0] - y[0] || x[1] - y[1] || (x[2] < y[2] ? -1 : x[2] > y[2] ? 1 : 0);
  });
}

/** Lists whose values make up fabric descriptions and garment names. */
export const NAME_LISTS = new Set(["speciality", "cat", "atpv", "cvc", "structure", "category"]);

// ---- Fabric --------------------------------------------------------------------
export interface FabricData {
  speciality?: string; cat?: string; atpv?: string; cvc?: string;
  composition?: Record<string, number>;
  structure?: string; gsm?: number | null; widthCm?: number | null; colour?: string;
}

export function compositionTotal(c: Record<string, number> | undefined): number {
  return Object.values(c || {}).reduce((s, v) => s + (Number(v) || 0), 0);
}

/** Fabric composition text, e.g. "FR, CAT 2, Cotton: 88%, Nylon: 12%, Structure: Twill, GSM: 280" (as v2.0.41). */
export function fabricDescription(d: FabricData): string {
  const parts: string[] = [];
  for (const k of ["speciality", "cat", "atpv", "cvc"] as const) if (d[k]) parts.push(String(d[k]));
  for (const [k, label] of FABRIC_COMP_FIELDS) {
    const v = d.composition?.[k];
    if (v !== undefined && v !== null && !Number.isNaN(Number(v)) && Number(v) !== 0) parts.push(`${label}: ${Number(v)}%`);
  }
  if (d.structure) parts.push(`Structure: ${d.structure}`);
  if (d.gsm !== undefined && d.gsm !== null && String(d.gsm) !== "") parts.push(`GSM: ${d.gsm}`);
  return parts.join(", ");
}

/** The fabric description without fibre composition, for people who may not see composition (as v2.0.41 POs). */
export function fabricCommercialDescription(d: FabricData): string {
  const parts: string[] = [];
  for (const k of ["speciality", "cat", "atpv", "cvc"] as const) if (d[k]) parts.push(String(d[k]));
  if (d.structure) parts.push(`Structure: ${d.structure}`);
  if (d.gsm !== undefined && d.gsm !== null && String(d.gsm) !== "") parts.push(`GSM: ${d.gsm}`);
  return parts.join(", ");
}

/** Fibre composition as "65% Polyester / 35% Cotton" (largest first). */
export function compositionText(c: Record<string, number> | undefined): string {
  return FABRIC_COMP_FIELDS.map(([k, l]) => [l, Number(c?.[k]) || 0] as const).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([l, v]) => `${v}% ${l}`).join(" / ");
}
const withPrefix = (p: string, v: unknown) => { const t = String(v ?? "").trim(); return !t ? "" : new RegExp(`^${p}\\b`, "i").test(t) ? t : `${p} ${t}`; };
/** A fabric in a bill of materials: "IFR-EARC twill, 65% Polyester / 35% Cotton, 250 GSM, CAT 2, ATPV 8" — its speciality and
 *  structure (else its name), composition (only for people allowed to see it), GSM, then CAT / ATPV when set. */
export function fabricBomDescription(name: string, d: FabricData, withComposition: boolean): string {
  const head = [d.speciality, d.structure].map((x) => String(x ?? "").trim()).filter(Boolean).join(" ") || String(name || "").trim();
  const gsm = d.gsm !== undefined && d.gsm !== null && String(d.gsm) !== "" ? `${d.gsm} GSM` : "";
  return [head, withComposition ? compositionText(d.composition) : "", gsm, withPrefix("CAT", d.cat), withPrefix("ATPV", d.atpv)].filter(Boolean).join(", ");
}
/** What a fabric is made of, for a materials card: "65% Polyester / 35% Cotton, 250 GSM". */
export function fabricMaterialText(d: FabricData, withComposition: boolean): string {
  const gsm = d.gsm !== undefined && d.gsm !== null && String(d.gsm) !== "" ? `${d.gsm} GSM` : "";
  return [withComposition ? compositionText(d.composition) : "", gsm].filter(Boolean).join(", ");
}

/** Two fabrics with exactly the same specification are the same fabric (as v2.0.41). */
export function fabricSpecKey(d: FabricData): string {
  const t = (v: unknown) => String(v ?? "").trim().replace(/\s+/g, " ").toLowerCase();
  const comp = Object.fromEntries(FABRIC_COMP_FIELDS.map(([k]) => [k, Number(d.composition?.[k] || 0)]));
  return JSON.stringify({ s: t(d.speciality), c: t(d.cat), a: t(d.atpv), v: t(d.cvc), comp, st: t(d.structure), g: d.gsm === null || d.gsm === undefined || String(d.gsm) === "" ? "" : Number(d.gsm) });
}

export function nextCode(prefix: string, existing: string[], width = 3): string {
  const re = new RegExp(`^${prefix}(\\d+)$`, "i");
  const max = existing.reduce((m, c) => { const x = re.exec(String(c).trim()); return x ? Math.max(m, Number(x[1])) : m; }, 0);
  return `${prefix}${String(max + 1).padStart(width, "0")}`;
}
export const CODE_PREFIX: Record<ItemType, string> = { product: "PRD", fabric: "FAB", trim: "TRM" };

// ---- Garment ------------------------------------------------------------------
export interface PackingProfile {
  pieceWeightKg: number; pcsPerCarton: number; cartonLengthCm: number; cartonWidthCm: number; cartonHeightCm: number;
  cartonNetWeightKg: number; cartonGrossWeightKg: number; cartonCbm: number;
  /** Fragile: in Container loading nothing that isn't fragile goes on top of these cartons (fragile on fragile is fine). */
  fragile: boolean;
  /** At most this many cartons stacked on top of one carton (0 = no limit). */
  maxOnTop: number;
}
const pos = (v: unknown) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0; };
export function normalizePacking(p: any = {}): PackingProfile {
  const x: PackingProfile = {
    pieceWeightKg: pos(p?.pieceWeightKg), pcsPerCarton: Math.floor(pos(p?.pcsPerCarton)),
    cartonLengthCm: pos(p?.cartonLengthCm), cartonWidthCm: pos(p?.cartonWidthCm), cartonHeightCm: pos(p?.cartonHeightCm),
    cartonNetWeightKg: pos(p?.cartonNetWeightKg), cartonGrossWeightKg: pos(p?.cartonGrossWeightKg), cartonCbm: 0,
    fragile: p?.fragile === true || p?.fragile === "true" || p?.fragile === 1, maxOnTop: Math.min(99, Math.floor(pos(p?.maxOnTop))),
  };
  const dims = x.cartonLengthCm * x.cartonWidthCm * x.cartonHeightCm;
  x.cartonCbm = dims ? Math.round((dims / 1_000_000) * 10000) / 10000 : pos(p?.cartonCbm);
  return x;
}
/**
 * Cartons, CBM and gross weight for a quantity packed to a profile — one calculation for Container Planning and landed
 * cost. Cartons = quantity ÷ pieces per carton, rounded up (unless the actual number is given); CBM = cartons × L × W × H
 * ÷ 1,000,000 (or a manual override for mixed / assorted cartons).
 */
export function cartonMetrics(qty: number, p: PackingProfile, actual: { cartons?: unknown; cbmOverride?: unknown } = {}) {
  const given = Number(actual.cartons);
  const cartons = Number.isFinite(given) && given > 0 ? Math.round(given) : p.pcsPerCarton > 0 ? Math.ceil(qty / p.pcsPerCarton) : 0;
  const dimsCm3 = p.cartonLengthCm * p.cartonWidthCm * p.cartonHeightCm;
  const override = Number(actual.cbmOverride);
  const cbm = Number.isFinite(override) && override > 0 && actual.cbmOverride !== null && actual.cbmOverride !== "" ? override : cartons && (dimsCm3 || p.cartonCbm) ? Math.round(cartons * (dimsCm3 ? dimsCm3 / 1_000_000 : p.cartonCbm) * 10000) / 10000 : 0;
  return { cartons, cbm, grossKg: cartons && p.cartonGrossWeightKg ? Math.round(cartons * p.cartonGrossWeightKg * 100) / 100 : 0, dimsCm3 };
}
/** "Fragile" / "Fragile, max 2 on top" / "Max 3 on top" / "" (normal stacking). */
export function stackingText(p: Pick<PackingProfile, "fragile" | "maxOnTop"> | null | undefined): string {
  if (!p) return "";
  const max = Math.floor(Number(p.maxOnTop) || 0);
  if (p.fragile) return max > 0 ? `Fragile, max ${max} on top` : "Fragile";
  return max > 0 ? `Max ${max} on top` : "";
}
export function packingSummary(p: PackingProfile): string {
  const parts: string[] = [];
  if (p.pcsPerCarton) parts.push(`${p.pcsPerCarton} pcs/ctn`);
  if (p.cartonCbm) parts.push(`${p.cartonCbm.toFixed(3)} m³`);
  if (p.cartonGrossWeightKg) parts.push(`${p.cartonGrossWeightKg.toFixed(2)} kg gross`);
  const stack = stackingText(p);
  if (stack) parts.push(stack);
  return parts.join(" · ");
}

function namePart(label: string, value: unknown): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const clean = raw.replace(new RegExp(`^${label}\\s*[:\\-]?\\s*`, "i"), "").trim();
  return clean ? `${label} ${clean}` : label;
}
/** The garment's "Add composition" / "Add GSM" choices (Add / Edit Garment → Details), saved per garment. */
export interface ProductNameOptions { addComposition?: boolean; addGsm?: boolean }
/** The main fabric's GSM as printed in a name: "250 GSM" ("" when the fabric has none). */
export const gsmText = (f: FabricData | null | undefined) => (f && f.gsm !== undefined && f.gsm !== null && String(f.gsm).trim() !== "" && Number(f.gsm) > 0 ? `${Number(f.gsm)} GSM` : "");
/** What "Add composition" / "Add GSM" add to the name: ["88% Cotton / 12% Nylon", "250 GSM"] (only what is ticked and known);
 *  a trade-secret fabric adds its label wording instead of the composition. */
export function productNameExtras(fabric: FabricData | null | undefined, o: ProductNameOptions | null | undefined): string[] {
  // A trade-secret fabric adds its label wording, never the full composition.
  const secret = (fabric as any)?.compositionSecret === true;
  const comp = secret ? String((fabric as any)?.labelWording ?? "").trim() : compositionText(fabric?.composition);
  return [o?.addComposition ? comp : "", o?.addGsm ? gsmText(fabric) : ""].filter(Boolean);
}
/** Garment name built from the main fabric, category and custom name (as v2.0.41), then — when ticked on the garment —
 *  ", " + the main fabric's composition (highest percentage first) and ", " + its GSM:
 *  "FR Shirt Hawk CAT 2 ATPV 12 cal/cm², 88% Cotton / 12% Nylon, 250 GSM". */
export function generatedProductName(category: string, customName: string, fabric: FabricData | null, o?: ProductNameOptions | null): string {
  const f = fabric || {};
  const base = [String(f.speciality || "").trim(), String(category || "").trim(), String(customName || "").trim(),
    namePart("CAT", f.cat), namePart("ATPV", f.atpv), namePart("CVC", f.cvc)].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  return base ? [base, ...productNameExtras(fabric, o)].join(", ") : "";
}

export function sizeRange(sizes: string[] | undefined): string {
  const v = (sizes || []).map((s) => String(s).trim()).filter(Boolean);
  if (!v.length) return "";
  return v.length === 1 ? v[0] : `${v[0]}–${v[v.length - 1]}`;
}

// ---- Bill of materials and costing ----------------------------------------------
export interface BomLineInput { componentId: string; quantity: number; wastePercent: number; placement?: string; isMain?: boolean }
export interface CostingComponent { code: string; name: string; type: ItemType; unit: string; quantity: number; wastePercent: number; unitCost: number | null }
export interface Costing {
  lines: Array<CostingComponent & { effectiveQty: number; lineCost: number | null }>;
  materials: number;
  making: number;
  other: number;
  total: number;
  missingCosts: number;         // components with no cost entered
  price: number | null;
  margin: number | null;        // price - total
  marginPercent: number | null; // margin as % of price
}
const r4 = (n: number) => Math.round(n * 10000) / 10000;

/** Garment cost = Σ (component cost × quantity × (1 + waste %)) + making cost + other costs. */
export function computeCosting(components: CostingComponent[], makingCost: number | null, otherCost: number | null, price: number | null): Costing {
  let materials = 0, missing = 0;
  const lines = components.map((c) => {
    const effectiveQty = r4(c.quantity * (1 + (c.wastePercent || 0) / 100));
    const lineCost = c.unitCost === null || c.unitCost === undefined ? null : r4(effectiveQty * c.unitCost);
    if (lineCost === null) missing++;
    else materials += lineCost;
    return { ...c, effectiveQty, lineCost };
  });
  const making = Number(makingCost || 0), other = Number(otherCost || 0);
  const total = r4(materials + making + other);
  const margin = price === null || price === undefined ? null : r4(price - total);
  return {
    lines, materials: r4(materials), making, other, total, missingCosts: missing,
    price: price ?? null, margin, marginPercent: margin === null || !price ? null : Math.round((margin / price) * 1000) / 10,
  };
}

/** Materials needed for an order quantity, e.g. 1,000 coveralls → 1,800 m of FAB003. */
export function materialRequirements(components: CostingComponent[], orderQty: number) {
  return components.map((c) => ({ code: c.code, name: c.name, unit: c.unit, perPiece: r4(c.quantity * (1 + (c.wastePercent || 0) / 100)), total: r4(orderQty * c.quantity * (1 + (c.wastePercent || 0) / 100)) }));
}

// ---- Custom fields ------------------------------------------------------------------
export const FIELD_TYPES = [["text", "Text"], ["number", "Number"], ["dropdown", "Dropdown"], ["multiselect", "Multi-select"], ["date", "Date"], ["yesno", "Yes / No"], ["file", "File"]] as const;
export type FieldType = (typeof FIELD_TYPES)[number][0];
export interface CustomField {
  id: string; key: string; label: string; type: FieldType; itemTypes: ItemType[]; categories: string[];
  required: boolean; defaultValue: unknown; unit: string; options: string[];
  showInList: boolean; showAsFilter: boolean; showOnDatasheet: boolean; sortOrder: number; active: boolean;
}

export function fieldApplies(f: CustomField, type: ItemType, category: string): boolean {
  if (!f.active || !f.itemTypes.includes(type)) return false;
  return !f.categories.length || f.categories.some((c) => c.toLowerCase() === String(category || "").toLowerCase());
}

/** Check and tidy custom field values for one item. Returns the clean values or a list of problems. */
export function validateCustom(fields: CustomField[], type: ItemType, category: string, input: Record<string, unknown>): { values: Record<string, unknown>; problems: string[] } {
  const values: Record<string, unknown> = {};
  const problems: string[] = [];
  for (const f of fields) {
    if (!fieldApplies(f, type, category)) continue;
    let v = input?.[f.key];
    const empty = v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length);
    if (empty) {
      if (f.required) problems.push(`${f.label} is required.`);
      continue;
    }
    switch (f.type) {
      case "number": {
        const n = Number(v);
        if (!Number.isFinite(n)) { problems.push(`${f.label} must be a number.`); continue; }
        v = n;
        break;
      }
      case "dropdown":
        if (!f.options.includes(String(v))) { problems.push(`${f.label}: choose one of the listed options.`); continue; }
        v = String(v);
        break;
      case "multiselect": {
        const arr = (Array.isArray(v) ? v : [v]).map(String);
        const bad = arr.filter((x) => !f.options.includes(x));
        if (bad.length) { problems.push(`${f.label}: "${bad[0]}" is not one of the options.`); continue; }
        v = arr;
        break;
      }
      case "date":
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(v))) { problems.push(`${f.label} must be a date.`); continue; }
        v = String(v);
        break;
      case "yesno":
        v = v === true || v === "true" || v === "yes" || v === "on";
        break;
      default:
        v = String(v).slice(0, 2000);
    }
    values[f.key] = v;
  }
  return { values, problems };
}

export function fieldKeyFromLabel(label: string): string {
  const base = String(label || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);
  return base ? `cf_${base}` : "";
}

// ---- Product datasheets (Settings → Product Datasheets), same settings and limits as v2.0.41 ----
export interface DatasheetSettings {
  template: string; defaultBrand: string; themeMode: "brand" | "custom"; primaryColor: string; secondaryColor: string; footerText: string;
  fabricDisplay: "code" | "full";
  productNameFontSize: number; headingFontSize: number; bodyFontSize: number; footerFontSize: number;
  specificationsSpacer: number; productFeaturesSpacer: number; careInstructionsSpacer: number; coloursSpacer: number; availableSizesSpacer: number;
  sectionHeaderColor: string; sectionBackgroundColor: string; sectionBorderColor: string;
  /** The optional second page: same header and footer as page 1, then the organisation's own writing and pictures. */
  page2: DatasheetPage2;
  updatedAt: string; updatedBy: string;
}
/** Where a part sits on the datasheet's second page: left, top, width and height as a percentage of the space between
 *  the header and the footer (so the layout is the same on screen, in print and in the PDF). */
export interface DatasheetBox { x: number; y: number; w: number; h: number }
/** One part of the datasheet's second page: a text box (an optional heading and the text) or a picture with an
 *  optional caption, each placed and sized freely (dragged on the preview). */
export type DatasheetBlock =
  | ({ type: "text"; heading: string; text: string } & DatasheetBox)
  | ({ type: "image"; fileId: string; caption: string } & DatasheetBox);
export interface DatasheetPage2 { enabled: boolean; blocks: DatasheetBlock[] }
export const DATASHEET_PAGE2_MAX_BLOCKS = 12;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const pct = (v: unknown, d: number, min: number, max: number) => { const n = Number(v); return Math.round((Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : d) * 10) / 10; };
/** A part's place on the page, kept inside the page (at least 10% wide and 5% high). Parts without one go under the
 *  part before (`below` is where the previous part ends). */
export function datasheetBox(b: any, below: number, type: "text" | "image"): DatasheetBox {
  const has = ["x", "y", "w", "h"].every((k) => Number.isFinite(Number(b?.[k])) && b?.[k] !== null && b?.[k] !== "");
  const w = pct(has ? b.w : 100, 100, 10, 100), h = pct(has ? b.h : type === "image" ? 45 : 25, 25, 5, 100);
  const x = pct(has ? b.x : 0, 0, 0, 100 - w), y = pct(has ? b.y : Math.min(below + (below ? 2 : 0), 100 - h), 0, 0, 100 - h);
  return { x, y, w, h };
}
export function normalizeDatasheetPage2(x: any = {}): DatasheetPage2 {
  const blocks: DatasheetBlock[] = [];
  let below = 0;
  for (const b of Array.isArray(x?.blocks) ? x.blocks : []) {
    if (blocks.length >= DATASHEET_PAGE2_MAX_BLOCKS) break;
    if (b?.type === "image") {
      if (!UUID_RE.test(String(b.fileId || ""))) continue;
      const box = datasheetBox(b, below, "image");
      blocks.push({ type: "image", fileId: String(b.fileId), caption: String(b.caption || "").slice(0, 200), ...box });
    } else if (b?.type === "text") {
      const box = datasheetBox(b, below, "text");
      blocks.push({ type: "text", heading: String(b.heading || "").slice(0, 120), text: String(b.text || "").replace(/\r\n?/g, "\n").slice(0, 4000), ...box });
    } else continue;
    below = Math.max(below, blocks[blocks.length - 1].y + blocks[blocks.length - 1].h);
  }
  return { enabled: !!x?.enabled, blocks };
}
/** [key, label, default, min, max] */
export const DATASHEET_FONTS: ReadonlyArray<readonly [keyof DatasheetSettings, string, number, number, number]> = [
  ["productNameFontSize", "Product name", 23, 14, 34], ["headingFontSize", "Section headings and product code", 12, 8, 18],
  ["bodyFontSize", "Body text", 9, 7, 13], ["footerFontSize", "Footer text", 8, 7, 16],
];
/** [key, label, default, max] in millimetres */
export const DATASHEET_SPACERS: ReadonlyArray<readonly [keyof DatasheetSettings, string, number, number]> = [
  ["specificationsSpacer", "After Fabric Specifications", 4, 15], ["productFeaturesSpacer", "After Product Features", 4, 10],
  ["careInstructionsSpacer", "After Care Instructions", 4, 10], ["coloursSpacer", "After Colours", 4, 10], ["availableSizesSpacer", "After Available Sizes", 0, 10],
];
export const DATASHEET_COLOURS: ReadonlyArray<readonly [keyof DatasheetSettings, string, string]> = [
  ["sectionHeaderColor", "Heading background", "#c62828"], ["sectionBackgroundColor", "Box background", "#eef1f4"], ["sectionBorderColor", "Box outline", "#9aa6b2"],
];
const hexOr = (v: unknown, d: string) => (/^#[0-9a-f]{6}$/i.test(String(v ?? "")) ? String(v) : d);
const clampNum = (v: unknown, d: number, min: number, max: number) => { const n = Number(v); return Number.isFinite(n) && v !== "" && v !== null ? Math.min(max, Math.max(min, n)) : d; };
export function normalizeDatasheetSettings(x: any = {}): DatasheetSettings {
  const out: any = {
    template: "Performance A4", defaultBrand: String(x?.defaultBrand || "").slice(0, 120), themeMode: x?.themeMode === "custom" ? "custom" : "brand",
    primaryColor: hexOr(x?.primaryColor, "#d7192d"), secondaryColor: hexOr(x?.secondaryColor, "#3b3839"), footerText: String(x?.footerText || "").slice(0, 200),
    fabricDisplay: x?.fabricDisplay === "full" ? "full" : "code", updatedAt: String(x?.updatedAt || ""), updatedBy: String(x?.updatedBy || ""),
  };
  for (const [k, , d, min, max] of DATASHEET_FONTS) out[k] = clampNum(x?.[k], d, min, max);
  for (const [k, , d, max] of DATASHEET_SPACERS) out[k] = clampNum(x?.[k], d, 0, max);
  for (const [k, , d] of DATASHEET_COLOURS) out[k] = hexOr(x?.[k], d);
  out.page2 = normalizeDatasheetPage2(x?.page2);
  return out;
}
